<template>
  <BaseTopBar
    v-if="isMounted"
    v-hotkey="shortcuts"
    :left-panel-width="leftPanelWidth"
    :is-navigation-displayed="isNavigationDisplayed"
    class="recurrent-task-topbar"
    with-find
    @find="$emit('find')"
  >
    <template #left>
      <Breadcrumb
        :items="hierarchy || []"
        :remove-parent-action="removeParentAction"
        :home="breadcrumbHome"
      />
    </template>
    <template #right>
      <!--    Occurences infos      -->
      <div class="occurrences">
        <span>{{ displayRecurrenceDate }}</span>
      </div>
      <BaseButton
        v-for="action in mainActions"
        :key="action.text"
        class="context-action"
        kind="auxiliary"
        :text="action.text"
        @click="getActionFunc(action, $event)"
      >
        <template #icon>
          <component :is="action.icon" />
        </template>
      </BaseButton>
      <ShareButton :export-action="onExport" :tint="'none'" />
    </template>
    <template #more>
      <div
        :ref="
          (el) => {
            if (el) {
              setTriggerMoreActionsEl(
                (el as { $el?: HTMLElement }).$el || (el as HTMLElement)
              );
            }
          }
        "
      />
      <BaseButton
        v-for="action in moreActions"
        :key="action.text"
        class="context-action"
        kind="auxiliary"
        :text="action.text"
        :tint="action.tint || 'none'"
        is-expanded
        @click="getActionFunc(action, $event)"
      >
        <template #icon> <component :is="action.icon" /> </template
        ><template #right>
          {{ action.shortcutsDisplay }}
        </template>
      </BaseButton>
    </template>
  </BaseTopBar>
</template>
<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  ref,
  toRefs,
} from "vue";

import controller from "@ctrl/controller";
import type { RecurrentTask } from "@ctrl/controller-rpc";
import { Recurrence } from "@ctrl/controller-rpc";

import { recurrentTaskActions } from "@/components/actions/actions";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseTopBar from "@/components/base/BaseTopBar.vue";
import Breadcrumb, { type BreadcrumbHome } from "@/components/Breadcrumb.vue";
import { RecurrentTaskContextActions } from "@/components/context-menu/context-actions";
import { formatRelativeDate } from "@/components/dates";
import RecurringIcon from "@/components/icons/RecurringIcon.vue";
import * as inject from "@/components/inject";
import { createBuiltinExportAction } from "@/components/modals/export";
import { getRecurrentTaskShortcuts } from "@/components/object-shortcuts";
import { isStopped as isRecurrenceStopped } from "@/components/recurrence";
import ShareButton from "@/components/ShareButton.vue";
import {
  assignShortcuts,
  selectAll as selectAllShortcut,
  Shortcuts,
} from "@/components/shortcuts";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import { computeHierarchy, detachFromParent } from "@/utils/hierarchy";
import { makeMove } from "@/utils/move";
import { navigateToHome } from "@/utils/navigate";

const {
  nextOccurrences: nextOccurrencesTrimmed,
  occurrenceCount,
  ppRecurrence,
} = controller;

export default defineComponent({
  components: {
    BaseTopBar,
    BaseButton,
    RecurringIcon,
    Breadcrumb,
    ShareButton,
  },
  props: {
    hasBlockActive: { type: Boolean, required: true },
    isNavigationDisplayed: { type: Boolean, default: true },
    leftPanelWidth: { type: Number, default: 0 },
    recurrentTask: { type: Object as PropType<RecurrentTask>, required: true },
  },
  emits: {
    find: () => true,
    flush: () => true,
    selectAll: () => true,
  },
  async setup(props, { emit }) {
    const { hasBlockActive, recurrentTask } = toRefs(props);
    const controller = inject.controller();
    const profile = inject.profile();
    const logger = inject.logger();
    const destinationPicker = inject.destinationPicker();
    const move = makeMove(controller, profile, logger, destinationPicker);
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const recurrencePicker = inject.recurrencePicker();
    const router = inject.router();
    const simpleModal = inject.simpleModal();
    const menu = inject.menu();
    const currentWorkspaceId = inject.currentWorkspaceId();
    const flush = () => emit("flush");
    const selectAll = () => emit("selectAll");
    // FIXME: Mandatory due to BaseTopBar teleportation
    const isMounted = ref(false);
    onMounted(() => (isMounted.value = true));
    const releaser = useReleaser();
    const hierarchy = await releaser.add(
      bindAsync(recurrentTask, async (recurrentTask) =>
        releasableRef(
          (await computeHierarchy(recurrentTask, controller, profile)).value
        )
      )
    );

    const breadcrumbHome = computed((): BreadcrumbHome | undefined => {
      const ws = currentWorkspaceId.value;
      return ws ? { type: "workspace", id: ws } : undefined;
    });

    const moreActionsButtonContainerEl = ref<HTMLElement>();
    const setTriggerMoreActionsEl = (el: HTMLElement) => {
      moreActionsButtonContainerEl.value = el;
    };
    const isStopped = computed<boolean>(() =>
      isRecurrenceStopped(recurrentTask.value)
    );
    const nextOccurrences = computed(() =>
      nextOccurrencesTrimmed(4, recurrentTask.value.recurrence, null)
    );
    const displayRecurrenceDate = computed<string>(() =>
      recurrentTask.value.recurrence
        ? ppRecurrence({ rule: recurrentTask.value.recurrence.rule }, false)
        : "Make recurrent"
    );
    const nbrOccurrencesBetween = computed(() => {
      //The goal is to find the number of occurrences after the 4 that are displayed and before the last (so -5)
      const totalOccurrences = occurrenceCount(recurrentTask.value.recurrence);
      if (totalOccurrences) {
        return totalOccurrences - 5 > 0 ? totalOccurrences - 5 : null;
      }
      return null;
    });
    const displayEndDate = computed(() => {
      const nbrOccurrences = occurrenceCount(recurrentTask.value.recurrence);
      return nbrOccurrences ? nbrOccurrences > 4 : false;
    });
    const mainActions = computed<RecurrentTaskContextActions[]>(() => [
      isStopped.value
        ? recurrentTaskActions.resume
        : recurrentTaskActions.pause,
    ]);
    const moreActions = computed<RecurrentTaskContextActions[]>(() => [
      recurrentTaskActions.editRecurrence,
      recurrentTaskActions.move,
      recurrentTaskActions.copyLink,
      { ...recurrentTaskActions.delete, tint: "red" },
    ]);
    const getActionFunc = (
      action: RecurrentTaskContextActions,
      event?: MouseEvent
    ) => {
      let clickedTriggerRef = moreActionsButtonContainerEl;
      if (event?.currentTarget && event.currentTarget instanceof HTMLElement) {
        const localRef = ref<HTMLElement>(event.currentTarget);
        clickedTriggerRef = localRef;
      }
      return action.action(
        { type: "task-rec", value: recurrentTask.value },
        {
          controller,
          logger,
          profile,
          move,
          router,
          simpleModal,
          menu,
          recurrencePickerInject: {
            component: recurrencePicker,
            // Use the clicked button as the anchor for this invocation
            triggerEl: clickedTriggerRef,
            onSelect: onSelectRecurrence,
            position: "right",
          },
          destinationPickerInject: {
            triggerEl: clickedTriggerRef,
            position: "right",
          },
          flush,
          clickEvent: event,
        },
        {
          beforeAction:
            action.text === recurrentTaskActions.delete.text
              ? async () => navigateToHome(router, menu)
              : undefined,
        }
      );
    };
    const removeParentAction = computed(() => ({
      onRemove: async () => {
        await detachFromParent(controller, recurrentTask.value);
      },
    }));
    const shortcuts = computed<Shortcuts>(() => {
      const injection = { isModalShown, isInputFocused };
      return hasBlockActive.value
        ? {}
        : {
            ...getRecurrentTaskShortcuts(
              recurrentTask.value,
              {
                ...injection,
                controller,
                logger,
                profile,
                move,
                router,
                recurrencePickerInject: {
                  component: recurrencePicker,
                  triggerEl: moreActionsButtonContainerEl,
                  onSelect: onSelectRecurrence,
                },
                simpleModal,
                menu,
              },
              {
                flush,
                beforeDeleteAction: async () => navigateToHome(router, menu),
              }
            ),
            ...assignShortcuts(injection, selectAllShortcut, () => {
              selectAll();
            }),
          };
    });

    const onExport = createBuiltinExportAction(controller, profile, () => ({
      title: recurrentTask.value.title,
      notes: recurrentTask.value.notes,
      extensionKind: "task",
      extensionRow: undefined,
    }));

    const onSelectRecurrence = async (recurrence: Recurrence) => {
      if (recurrentTask.value) {
        await controller.taskRecurrent.update(
          recurrentTask.value.id,
          {
            recurrence,
          },
          "app"
        );
        recurrencePicker.value.closePicker();
      }
    };

    return {
      displayEndDate,
      displayRecurrenceDate,
      formatRelativeDate,
      getActionFunc,
      hierarchy,
      breadcrumbHome,
      isMounted,
      isStopped,
      mainActions,
      moreActions,
      onExport,
      removeParentAction,
      nbrOccurrencesBetween,
      nextOccurrences,
      shortcuts,
      moreActionsButtonContainerEl,
      setTriggerMoreActionsEl,
    };
  },
});
</script>
<style lang="scss" scoped>
@use "@/styles/context-actions.scss";
.occurrences {
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  @include themed() {
    color: t($grey-50);
  }
}
</style>
